/* eslint-disable no-fallthrough */
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { PrecisionFloat } from '@bts-web/utils-formatting';
import { debounce } from '@bts-web/utils-functions';
import { MutableNonNullableAsset } from '../../types';
import { getAssetById } from '../../../../common/gqlActions/getAssetById.action';
import { useClientUserInfo } from '../../../../common/userinfo/InitialUserInfoFetcherAndParser/utils/useClientUserInfo';
import { useHandleGqlErrorsWithSnackbar, UserTypes } from '../../../../common';
import { TradeControllerVariants, TradeOperation } from '../../../utils';
import { getSwapAssetsDataAndExchangeRate } from '../../../../common/gqlActions/getSwapAssetsDataAndExchangeRate';
import { OrderOperation } from '../../../../orders/types';

const REFRESH_TIMEOUT_IN_MS = 15000;

const DEBOUNCE_DELAY_MS = 500;

type AssetStakeData = {
  totalBalanceAmount: PrecisionFloat;
  availableForStakingAmount: PrecisionFloat;
  currentlyStakingAmount: PrecisionFloat;
  currentlyUnstakingAmount: PrecisionFloat;
  rewardAssetAmount: PrecisionFloat;
};

export const useTradeDataRefresher = ({
  initialAssetData,
  transactionType,
  initialTargetAssetData,
  initialExchangeRate,
  orderOperation,
}: {
  initialTargetAssetData?: MutableNonNullableAsset;
  initialAssetData: MutableNonNullableAsset;
  transactionType: TradeControllerVariants;
  initialExchangeRate?: PrecisionFloat;
  assetStakeData?: AssetStakeData;
  orderOperation?: OrderOperation;
}) => {
  const [swapExchangeRate, setSwapExchangeRate] =
    useState<PrecisionFloat>(initialExchangeRate);

  const [assetCurrentData, setAssetCurrentData] =
    useState<MutableNonNullableAsset>(initialAssetData);

  const [targetAssetData, setTargetAssetData] = useState<
    MutableNonNullableAsset | undefined
  >(initialTargetAssetData);

  const [isDataRefreshing, setIsDataRefreshing] = useState(false);

  const userData = useClientUserInfo();

  const { processErrors } = useHandleGqlErrorsWithSnackbar();

  const isBuyOrSavingsPlanOperation =
    transactionType === TradeOperation.buy ||
    transactionType === TradeOperation.savings;

  const progressTimer = useRef<NodeJS.Timeout | null>(null);

  const isMountedRef = useRef(true);

  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchInProgressRef = useRef(false);

  const lastFetchTimeRef = useRef(0);

  const debouncedFetchRef = useRef<ReturnType<typeof debounce> | null>(null);

  const { role, userType, approved } = userData;

  const isBusinessUserAndNotApproved =
    userType === UserTypes.BUSINESS_USER && (!approved || role === 'READ_ONLY');

  const isOrderOperation = transactionType === TradeOperation.order;

  const price: PrecisionFloat = isOrderOperation
    ? assetCurrentData.price
    : isBuyOrSavingsPlanOperation
      ? assetCurrentData.priceBuy
      : assetCurrentData.priceSell;

  const isTradingActive = useMemo(() => {
    if (!initialAssetData.isActive) {
      return false;
    }

    switch (transactionType) {
      case TradeOperation.savings:

      case TradeOperation.buy:
        return initialAssetData.isBuyActive;

      case TradeOperation.order:
        return orderOperation === OrderOperation.SELL
          ? initialAssetData.isSellActive
          : initialAssetData.isBuyActive;

      case TradeOperation.stake:
        return initialAssetData.staking?.enabled;

      case TradeOperation.unstake:
        return initialAssetData.staking?.enabled;

      case TradeOperation.sell:
        return initialAssetData.isSellActive;

      case TradeOperation.swap:
        return Boolean(
          initialAssetData.isSellActive === true &&
            initialTargetAssetData?.isBuyActive === true,
        );

      default:
        return false;
    }
  }, [
    initialAssetData,
    transactionType,
    initialTargetAssetData,
    orderOperation,
  ]);

  const fetchCurrentData = useCallback(async () => {
    if (!isMountedRef.current || fetchInProgressRef.current) return;

    const now = Date.now();

    if (now - lastFetchTimeRef.current < 1000) return;

    fetchInProgressRef.current = true;

    lastFetchTimeRef.current = now;

    try {
      const { data, errors } = await getAssetById(initialAssetData.id);

      if (!isMountedRef.current) return;

      if (errors) {
        processErrors(errors);

        return;
      }

      if (data) {
        setAssetCurrentData(data);
      }
    } catch (error) {
      if (!isMountedRef.current) return;

      console.error('Error fetching asset data:', error);
    } finally {
      if (isMountedRef.current) {
        setIsDataRefreshing(false);

        fetchInProgressRef.current = false;
      }
    }
  }, [initialAssetData.id]);

  const fetchSwapAssetsAndExchangeRate = useCallback(
    async (sourceAssetId: string, targetAssetId: string) => {
      if (!isMountedRef.current || fetchInProgressRef.current) return;

      const now = Date.now();

      if (now - lastFetchTimeRef.current < 1000) return;

      fetchInProgressRef.current = true;

      lastFetchTimeRef.current = now;

      try {
        const { data, errors } = await getSwapAssetsDataAndExchangeRate({
          sourceAssetId,
          targetAssetId,
        });

        if (!isMountedRef.current) return;

        if (errors) {
          processErrors(errors);

          return;
        }

        const { asset, exchangeRate, targetAsset } = data ?? {};

        if (asset) {
          setAssetCurrentData(asset);
        }

        if (targetAsset) {
          setTargetAssetData(targetAsset);
        }

        if (exchangeRate !== undefined) {
          setSwapExchangeRate(exchangeRate);
        }
      } catch (error) {
        if (!isMountedRef.current) return;

        console.error('Error fetching swap data:', error);
      } finally {
        if (isMountedRef.current) {
          setIsDataRefreshing(false);

          fetchInProgressRef.current = false;
        }
      }
    },
    [],
  );

  const fetchData = useCallback(() => {
    if (!isMountedRef.current || fetchInProgressRef.current) return;

    setIsDataRefreshing(true);

    if (transactionType === TradeOperation.swap && initialTargetAssetData?.id) {
      fetchSwapAssetsAndExchangeRate(
        initialAssetData.id,
        initialTargetAssetData.id,
      );
    } else {
      fetchCurrentData();
    }
  }, [transactionType, initialTargetAssetData?.id, initialAssetData.id]);

  useEffect(() => {
    isMountedRef.current = true;

    fetchInProgressRef.current = false;

    lastFetchTimeRef.current = 0;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    if (!debouncedFetchRef.current) {
      debouncedFetchRef.current = debounce(fetchData, DEBOUNCE_DELAY_MS);
    }

    if (
      !isBusinessUserAndNotApproved &&
      userData.tradingActive &&
      isTradingActive
    ) {
      progressTimer.current = setInterval(() => {
        if (!fetchInProgressRef.current) {
          debouncedFetchRef.current?.();
        }
      }, REFRESH_TIMEOUT_IN_MS);
    }

    return () => {
      isMountedRef.current = false;

      fetchInProgressRef.current = false;

      if (progressTimer.current) {
        clearInterval(progressTimer.current);

        progressTimer.current = null;
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();

        abortControllerRef.current = null;
      }

      if (debouncedFetchRef.current) {
        debouncedFetchRef.current.cancel?.();
      }
    };
  }, [
    initialAssetData?.id,
    initialTargetAssetData?.id,
    transactionType,
    isBusinessUserAndNotApproved,
    userData.tradingActive,
    isTradingActive,
  ]);

  useEffect(() => {
    if (transactionType !== TradeOperation.swap) {
      setTargetAssetData(undefined);

      setSwapExchangeRate(undefined);
    }
  }, [transactionType]);

  useEffect(() => {
    setAssetCurrentData(initialAssetData);
  }, [initialAssetData]);

  useEffect(() => {
    setTargetAssetData(initialTargetAssetData);
  }, [initialTargetAssetData]);

  useEffect(() => {
    setSwapExchangeRate(initialExchangeRate);
  }, [initialExchangeRate]);

  return {
    isDataRefreshing,
    assetCurrentData,
    targetAssetData,
    swapExchangeRate,
    isTradingActive,
    isBuyOrSavingsPlanOperation,
    price,
    assetStakeData: undefined, // To be handled in scope of BU-3858
  };
};
